export const RESEND_COOLDOWN_SECONDS = 60;
export const OTP_VALIDITY_SECONDS = 5 * 60;
const DEFAULT_COOLDOWN_SECONDS = RESEND_COOLDOWN_SECONDS;

export interface OtpCountdownState {
  readonly resendSeconds: number;
  readonly validitySeconds: number;
}

/** Formats a remaining duration as `m:ss` so 5 minutes is not confused with a 60s resend wait. */
export function formatCountdown(totalSeconds: number): string {
  const safeSeconds: number = Math.max(0, totalSeconds);
  const minutes: number = Math.floor(safeSeconds / 60);
  const seconds: number = safeSeconds % 60;
  return `${String(minutes)}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Visible resend cooldown matching the Auth frontend throttle (60s between sends).
 * Returns a stop function for ngOnDestroy.
 */
export function startRequestCooldown(onTick: (remainingSeconds: number) => void, seconds: number = DEFAULT_COOLDOWN_SECONDS): () => void {
  let remainingSeconds: number = seconds;
  onTick(remainingSeconds);
  const timerId: ReturnType<typeof setInterval> = setInterval(() => {
    remainingSeconds -= 1;
    onTick(Math.max(0, remainingSeconds));
    if (remainingSeconds <= 0) {
      clearInterval(timerId);
    }
  }, 1000);
  return (): void => {
    clearInterval(timerId);
  };
}

/**
 * Runs the 60s resend wait and the 5-minute OTP validity in one interval.
 * Validity matches `OTP_TTL_MS` in erpclass-auth.
 */
export function startOtpCountdowns(onTick: (state: OtpCountdownState) => void): () => void {
  let resendSeconds: number = RESEND_COOLDOWN_SECONDS;
  let validitySeconds: number = OTP_VALIDITY_SECONDS;
  onTick({ resendSeconds, validitySeconds });
  const timerId: ReturnType<typeof setInterval> = setInterval(() => {
    if (resendSeconds > 0) {
      resendSeconds -= 1;
    }
    if (validitySeconds > 0) {
      validitySeconds -= 1;
    }
    onTick({ resendSeconds, validitySeconds });
    if (resendSeconds <= 0 && validitySeconds <= 0) {
      clearInterval(timerId);
    }
  }, 1000);
  return (): void => {
    clearInterval(timerId);
  };
}
